import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, Legend } from 'recharts';
import { backupAPI } from '../services/api';
import DataTable from '../components/DataTable';

const RETRY_COLS = [
  { key: 'customer_name',    label: 'Cliente' },
  { key: 'job_name',         label: 'Job' },
  { key: 'backup_type',      label: 'Tipo' },
  { key: 'retry_count',      label: 'N° Retry' },
  { key: 'max_retry_number', label: 'Max Retry' },
  { key: 'final_status',     label: 'Stato finale' },
  { key: 'end_time',         label: 'Data' },
];

function durationColor(min) {
  if (min < 30)  return '#16a34a';
  if (min < 68)  return '#ca8a04';
  return '#dc2626';
}

export default function PerformancePage({ customer }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    backupAPI.getPerformance(customer)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [customer]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Caricamento...</div>;
  if (!data)   return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Errore caricamento dati</div>;

  const durationData = (data.avg_duration || []).map(j => ({
    name:    j.job_name,
    media:   parseFloat(j.avg_minutes) || 0,
    massimo: j.max_minutes || 0,
  }));

  // Trend: pivot per job_name
  const jobNames  = [...new Set((data.duration_trend || []).map(t => t.job_name))];
  const trendMap  = {};
  (data.duration_trend || []).forEach(t => {
    const key = t.day;
    if (!trendMap[key]) trendMap[key] = { day: new Date(key).toLocaleDateString('it-IT', { day:'2-digit', month:'2-digit' }) };
    trendMap[key][t.job_name] = parseFloat(t.avg_minutes) || 0;
  });
  const trendData = Object.values(trendMap);
  const COLORS = ['#2563eb','#16a34a','#ca8a04','#dc2626','#7c3aed','#0891b2','#ea580c','#0f766e'];

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700, color: '#1e293b' }}>Performance</h2>

      {/* Durata media */}
      <div style={s.card}>
        <div style={s.cardTitle}>⏱️ Durata Media per Job — ultimi 7 giorni (minuti)</div>
        <div style={{ padding: '8px 16px 20px' }}>
          {durationData.length === 0
            ? <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Nessun dato negli ultimi 7 giorni</div>
            : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={durationData} margin={{ top: 10, right: 20, left: 0, bottom: 70 }}>
                  <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 11 }} interval={0} />
                  <YAxis tick={{ fontSize: 11 }} unit=" min" />
                  <Tooltip formatter={(v, n) => [`${v} min`, n === 'media' ? 'Media' : 'Massimo']} />
                  <Bar dataKey="media" name="Media" radius={[4,4,0,0]}>
                    {durationData.map((entry, i) => (
                      <Cell key={i} fill={durationColor(entry.media)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )
          }
          <div style={{ display: 'flex', gap: 20, fontSize: 12, color: '#64748b', paddingLeft: 8 }}>
            <span style={{ color: '#16a34a' }}>■ &lt; 30 min</span>
            <span style={{ color: '#ca8a04' }}>■ 30–68 min</span>
            <span style={{ color: '#dc2626' }}>■ &gt; 68 min</span>
          </div>
        </div>
      </div>

      {/* Trend durata */}
      {trendData.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>📈 Trend Durata per Job — ultimi 7 giorni</div>
          <div style={{ padding: '8px 16px 20px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trendData}>
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis unit=" min" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                {jobNames.slice(0, 8).map((name, i) => (
                  <Line key={name} type="monotone" dataKey={name}
                    stroke={COLORS[i % COLORS.length]} dot={false} strokeWidth={2} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top retry */}
      <DataTable
        title={`🔁 Top Retry — ultimi 30 giorni (${data.top_retry?.length ?? 0})`}
        columns={RETRY_COLS}
        rows={data.top_retry}
        emptyMsg="Nessun evento retry negli ultimi 30 giorni ✅"
      />
    </div>
  );
}

const s = {
  card:     { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 20 },
  cardTitle:{ padding: '14px 20px', fontWeight: 700, fontSize: 15, color: '#1e293b', borderBottom: '1px solid #f1f5f9' },
};
